import React, { useEffect, useState } from "react";
import axios from "axios";
import {
    Box,
    Grid,
    Card,
    Typography,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogContent,
    CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
    Megaphone,
    TrendingUp,
    Shield,
    IndianRupee,
    Eye,
    FileText,
    ArrowLeft,
    BarChart2,
    MapPin,
    Target,
    Calendar,
    CreditCard,
    Image,
} from "lucide-react";
import CloseIcon from "@mui/icons-material/Close";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Legend,
} from "recharts";
import { PlayArrow, Pause } from "@mui/icons-material";
import "./Promotiondashbord.css";
import { GrMail } from "react-icons/gr";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
// ─── helpers ──────────────────────────────────────────────────────────────────
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useRef } from "react";
// import { MAIN_API } from "../../../config/api";


function formatCurrency(amount) {
    return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 0,
    }).format(amount || 0);
}

function formatDate(date) {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("en-IN");
}

const getStatusColor = (status) => {
    if (status === "APPROVED") return "success";
    if (status === "PENDING") return "warning";
    if (status === "REJECTED") return "error";
    return "default";
};

// For inline badge inside detail 
const getStatusStyle = (status) => {
    if (status === "APPROVED") return { bg: "#dcfce7", color: "#15803d", border: "#bbf7d0" };
    if (status === "PENDING") return { bg: "#fef9c3", color: "#a16207", border: "#fde68a" };
    if (status === "REJECTED") return { bg: "#fee2e2", color: "#b91c1c", border: "#fecaca" };
    return { bg: "#f3f4f6", color: "#6b7280", border: "#e5e7eb" };
};

const PIE_COLORS = ["#f97316", "#fb923c", "#fdba74", "#fbbf24"];

// ─── DetailRow — inline CSS ───────────────────────────────────────────────────

function DetailRow({ label, value }) {
    return (
        <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            padding: "9px 0",
            borderBottom: "1px solid #f3f4f6",
        }}>
            <span style={{
                fontSize: "12px",
                color: "#9ca3af",
                flexShrink: 0,
                marginRight: "8px",
            }}>
                {label}
            </span>
            <span style={{
                fontSize: "12px",
                fontWeight: 600,
                color: "#1f2937",
                textAlign: "right",
                wordBreak: "break-word",
                maxWidth: "62%",
            }}>
                {value || "—"}
            </span>
        </div>
    );
}

// ─── SectionCard — inline CSS ─────────────────────────────────────────────────

function SectionCard({ icon: Icon, title, children, accentColor }) {
    return (
        <div style={{
            background: "#ffffff",
            borderRadius: "14px",
            border: accentColor ? `1.5px solid ${accentColor}35` : "1px solid #f0f0f0",
            overflow: "hidden",
            marginBottom: "16px",
            // boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        }}>
            {/* Header */}
            <div style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 16px",
                background: accentColor ? `${accentColor}0d` : "#fafafa",
                borderBottom: "1px solid #f0f0f0",
            }}>
                {Icon && (
                    <div style={{
                        width: "26px",
                        height: "26px",
                        borderRadius: "6px",
                        background: accentColor ? `${accentColor}20` : "#f3f4f6",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}>
                        <Icon size={13} color={accentColor || "#9ca3af"} />
                    </div>
                )}
                <span style={{
                    fontSize: "11px",
                    fontWeight: 700,
                    color: accentColor || "#6b7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.7px",
                }}>
                    {title}
                </span>
            </div>
            {/* Body */}
            <div style={{ padding: "4px 16px 8px" }}>
                {children}
            </div>
        </div>
    );
}

// ─── StatMiniCard — inline CSS ────────────────────────────────────────────────

function StatMiniCard({ label, value, color }) {
    return (
        <div style={{
            flex: "1 1 0",
            background: "#ffffff",
            border: "1px solid #f0f0f0",
            borderRadius: "14px",
            padding: "16px 12px",
            textAlign: "center",
            borderTop: `3px solid ${color}`,
            // boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        }}>
            <div style={{
                fontSize: "24px",
                fontWeight: 700,
                color,
                lineHeight: 1,
            }}>
                {typeof value === "number" ? value.toLocaleString("en-IN") : value}
            </div>
            <div style={{
                fontSize: "11px",
                color: "#9ca3af",
                marginTop: "5px",
                fontWeight: 500,
            }}>
                {label}
            </div>
        </div>
    );
}

// ─── CampaignDetailView — full inline CSS ─────────────────────────────────────

function CampaignDetailView({ campaign, onBack }) {
    const investment = campaign.totalBudget || 0;

    const [discountData, setDiscountData] = useState([]);
    const [summary, setSummary] = useState({});
    const [leadsData, setLeadsData] = useState([]);

    const thStyle = {
        textAlign: "left",
        padding: "10px",
        fontWeight: 600,
        color: "#fff",
        fontSize: "12px"
    };

    const tdStyle = {
        padding: "10px",
        color: "#111827",
        fontWeight: 500
    };

    // ================================
    // ✅ LEADS API (SEPARATE)
    // ================================
    const fetchLeads = () => {
        
    };

    // ================================
    // ✅ DISCOUNT API (SEPARATE & CORRECT)
    // ================================
    const fetchDiscountStats = () => {
       
    };

    // ================================
    // ✅ CALL BOTH APIs
    // ================================
    useEffect(() => {
        if (!campaign?.id) return;

        fetchLeads();
        fetchDiscountStats();

    }, [campaign?.id]);

    return (
        <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}>

            {/* HEADER */}
            <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                <button onClick={onBack} style={{
                    width: "36px",
                    height: "36px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "10px",
                    background: "#fff",
                    cursor: "pointer"
                }}>
                    ←
                </button>

                <div style={{ fontSize: "12px", color: "#9ca3af" }}>
                    {campaign.campaignCode}
                </div>
            </div>

            {/* STATS */}
            {campaign.goal === "DISCOUNT" ? (
                <div style={{
                    display: "flex",
                    gap: "12px",
                    marginBottom: "20px",
                    marginTop: "20px",
                    flexWrap: "wrap"
                }}>
                    <StatMiniCard
                        label="Total Users"
                        value={summary?.totalUsers ?? 0}
                        color="#f97316"
                    />
                    <StatMiniCard
                        label="Total Discount"
                        value={`₹ ${summary?.totalDiscount ?? 0}`}
                        color="#22c55e"
                    />
                </div>
            ) : (
                <div style={{
                    display: "flex",
                    gap: "12px",
                    marginBottom: "20px",
                    marginTop: "20px",
                    flexWrap: "wrap"
                }}>
                    <StatMiniCard label="Views" value={campaign?.viewsCount ?? 0} color="#f97316" />
                    <StatMiniCard label="Likes" value={campaign?.likesCount ?? 0} color="#ec4899" />
                    <StatMiniCard label="Leads" value={campaign?.leadsCount ?? 0} color="#8b5cf6" />
                    <StatMiniCard label="Shares" value={campaign?.sharesCount ?? 0} color="#06b6d4" />
                    <StatMiniCard label="Saves" value={campaign?.savesCount ?? 0} color="#22c55e" />
                </div>
            )}

            {/* ======================= */}
            {/* ✅ LEADS TABLE */}
            {/* ======================= */}
            {campaign.goal !== "DISCOUNT" && leadsData.length > 0 && (
                <div style={{ marginTop: "24px" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead style={{ background: "#e66d33", color: "#fff" }}>
                            <tr>
                                <th style={thStyle}>Lead ID</th>
                                <th style={thStyle}>Name</th>
                                <th style={thStyle}>Mobile</th>
                                <th style={thStyle}>Email</th>
                                <th style={thStyle}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {leadsData.map((lead, index) => (
                                <tr key={index}>
                                    <td style={tdStyle}>{lead.id || "-"}</td>
                                    <td style={tdStyle}>{lead.name || "-"}</td>
                                    <td style={tdStyle}>{lead.phoneNumber || "-"}</td>
                                    <td style={tdStyle}>{lead.email || "-"}</td>
                                    <td style={tdStyle}>{lead.callToAction || "-"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}



            {/* ======================= */}
            {/* ✅ DISCOUNT TABLE */}
            {/* ======================= */}
            {campaign.goal === "DISCOUNT" && discountData.length > 0 && (
                <div style={{ marginTop: "24px" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead style={{ background: "#e66d33", color: "#fff" }}>
                            <tr>
                                <th style={thStyle}>User ID</th>
                                <th style={thStyle}>User Name</th>
                                <th style={thStyle}>Saved Amount</th>
                                <th style={thStyle}>Code</th>
                                <th style={thStyle}>Coupon Type</th>
                                <th style={thStyle}>Discount Type</th>

                            </tr>
                        </thead>
                        <tbody>
                            {discountData.map((user, index) => (
                                <tr key={index}>
                                    <td style={tdStyle}>{user.userId}</td>
                                    <td style={tdStyle}>{user.userName}</td>
                                    <td style={tdStyle}>₹ {user.discountAmount}</td>
                                    <td style={tdStyle}>{user.code}</td>
                                    <td style={tdStyle}>{user.couponType}</td>
                                    <td style={tdStyle}>{user.discountType}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

        </div>
    );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────

export default function LeadsPricing({ onNavigate }) {
    const [campaigns, setCampaigns] = useState([]);
    const [selectedCampaign, setSelectedCampaign] = useState(null); // null = table view
    const [openInvoice, setOpenInvoice] = useState(false);
    const [invoiceData, setInvoiceData] = useState(null);
    const [loadingInvoice, setLoadingInvoice] = useState(false);
    const invoiceRef = useRef();
    const [openCreative, setOpenCreative] = useState(false);
    const [selectedCreative, setSelectedCreative] = useState("");
    const [goalFilter, setGoalFilter] = useState("all");
    const [dateFilter, setDateFilter] = useState("today");
    // const [campaigns, setCampaigns] = useState(initialCampaigns);
    const [openDatePopup, setOpenDatePopup] = useState(false);
    const [customStart, setCustomStart] = useState("");
    const [customEnd, setCustomEnd] = useState("");
    const [selectedMediaType, setSelectedMediaType] = useState("");
    const getFilteredCampaigns = () => {
        let data = [...campaigns];

        // ✅ GOAL FILTER
        if (goalFilter !== "all") {
            data = data.filter(
                c => (c.goal || "").toUpperCase() === goalFilter
            );
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const getCampaignDate = (c) => new Date(c.startDate); // ✅ SINGLE SOURCE

        // ✅ TODAY
        if (dateFilter === "today") {
            data = data.filter(c => {
                const d = getCampaignDate(c);
                return d.toDateString() === today.toDateString();
            });
        }

        // ✅ YESTERDAY
        if (dateFilter === "yesterday") {
            const yesterday = new Date(today);
            yesterday.setDate(today.getDate() - 1);

            data = data.filter(c => {
                const d = getCampaignDate(c);
                return d.toDateString() === yesterday.toDateString();
            });
        }

        // ✅ WEEK
        if (dateFilter === "week") {
            const weekAgo = new Date(today);
            weekAgo.setDate(today.getDate() - 7);

            data = data.filter(c => {
                const d = getCampaignDate(c);
                return d >= weekAgo && d <= new Date();
            });
        }

        // ✅ MONTH
        if (dateFilter === "month") {
            const monthAgo = new Date(today);
            monthAgo.setMonth(today.getMonth() - 1);

            data = data.filter(c => {
                const d = getCampaignDate(c);
                return d >= monthAgo && d <= new Date();
            });
        }

        // ✅ CUSTOM (START + END BASED)
        if (dateFilter === "custom" && customStart && customEnd) {
            const start = new Date(customStart);
            const end = new Date(customEnd);

            start.setHours(0, 0, 0, 0);
            end.setHours(23, 59, 59, 999);

            data = data.filter(c => {
                const campaignStart = new Date(c.startDate);
                const campaignEnd = new Date(c.endDate);

                // ✅ OVERLAP LOGIC (CORRECT)
                return (
                    campaignStart <= end &&
                    campaignEnd >= start
                );
            });
        }

        return data;
    };
    const handleToggleCampaign = (id) => {
        setCampaigns((prev) =>
            prev.map((item) =>
                item.id === id
                    ? {
                        ...item,
                        isPaused: !item.isPaused,
                    }
                    : item
            )
        );
    };
    const downloadInvoice = async () => {
        const element = invoiceRef.current;
        const canvas = await html2canvas(element);
        const imgData = canvas.toDataURL("image/png");

        const pdf = new jsPDF("p", "mm", "a4");
        const imgWidth = 190;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);
        pdf.save(`Invoice_${invoiceData.campaignCode}.pdf`);
    };
    // ── Fetch campaigns ───────────────────────────────────────────────────────
    useEffect(() => {
        const fetchCampaigns = () => {
           
        };

        fetchCampaigns();
    }, []);

    // ── Invoice handler ───────────────────────────────────────────────────────
    const handleOpenInvoice = () => {
       
    };

    // ── Stats ─────────────────────────────────────────────────────────────────
    const liveCampaigns = campaigns.filter(
        (c) => c.approvalStatus === "APPROVED"
    ).length;
    const pendingReview = campaigns.filter((c) => ["PENDING", "DRAFT"].includes(c.status)).length;
    const totalSpend = campaigns.reduce((sum, c) => sum + (c.totalBudget || 0), 0);

    const stats = [
        { label: "Total Leads", value: campaigns.length, icon: Megaphone },
        { label: "Approved Leads", value: liveCampaigns, icon: TrendingUp },
        { label: "Pending Review", value: pendingReview, icon: Shield },
        { label: "Total Spend", value: formatCurrency(totalSpend), icon: IndianRupee },
    ];

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Box className="dash-container" style={{ marginTop: "10px" }}>

            {/* ── Stats — always visible ── */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                {stats.map((stat, i) => {
                    const Icon = stat.icon;

                    return (
                        <Grid item xs={12} sm={6} md={3} key={i}>
                            <Card
                                sx={{
                                    borderRadius: "22px",
                                    p: 3,
                                    height: "140px",
                                    boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                                    border: "1px solid #edf2f7",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                    transition: "0.3s",
                                    "&:hover": {
                                        transform: "translateY(-4px)",
                                        boxShadow: "0 12px 28px rgba(0,0,0,0.12)"
                                    }
                                }}
                            >
                                {/* Top Section */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start"
                                    }}
                                >
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: "12px",
                                                color: "#475569",
                                                fontWeight: 600,
                                                letterSpacing: "0.5px",
                                                textTransform: "uppercase"
                                            }}
                                        >
                                            {stat.label}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: "30px",
                                                fontWeight: 700,
                                                color: "#0f172a",
                                                mt: 1,
                                                lineHeight: 1
                                            }}
                                        >
                                            {stat.value}
                                        </Typography>
                                    </Box>

                                    <Box
                                        sx={{
                                            width: 50,
                                            height: 50,
                                            borderRadius: "50%",
                                            background:
                                                stat.color === "orange"
                                                    ? "#FFF3E6"
                                                    : stat.color === "green"
                                                        ? "#EAFBF2"
                                                        : stat.color === "blue"
                                                            ? "#E8F5FF"
                                                            : "#F3F4F6",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center"
                                        }}
                                    >
                                        <Icon
                                            size={24}
                                            color={
                                                stat.color === "orange"
                                                    ? "#F97316"
                                                    : stat.color === "green"
                                                        ? "#10B981"
                                                        : stat.color === "blue"
                                                            ? "#0EA5E9"
                                                            : "#6B7280"
                                            }
                                        />
                                    </Box>
                                </Box>

                                {/* Bottom Growth Badge */}
                                {/* <Box
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        width: "fit-content",
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: "20px",
                                        background: "#DCFCE7",
                                        color: "#16A34A",
                                        fontWeight: 700,
                                        fontSize: "14px"
                                    }}
                                >
                                    ↗ {stat.growth}
                                </Box> */}
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            {/* ── Shared card: TABLE ↔ DETAIL ── */}
            <Card className="table-cards promotionsdashboard-data" sx={{ p: selectedCampaign ? 3 : 0 }} style={{ boxShadow: "none" }} >

                {/* TABLE VIEW */}
                {!selectedCampaign && (
                    <div>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end", // ✅ pushes to right
                                width: "100%"
                            }}
                        >

                            {/* 🔽 YOUR EXISTING FILTER BOX (no major changes) */}
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "end",
                                    gap: "16px",
                                    mb: 2,
                                    padding: "10px 14px",
                                    background: "#ffffff",
                                    borderRadius: "10px",
                                    border: "1px solid #f1f5f9",
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
                                }}
                            >



                                {/* 📅 DATE FILTER */}
                                <div style={{ display: "flex", flexDirection: "column" }}>


                                    <select
                                        value={dateFilter}
                                        onChange={(e) => {
                                            setDateFilter(e.target.value);
                                            if (e.target.value === "custom") {
                                                setOpenDatePopup(true);
                                            }
                                        }}
                                        style={{
                                            padding: "6px 10px",
                                            borderRadius: "6px",
                                            border: "1px solid #e5e7eb",
                                            fontSize: "13px",
                                            background: "#f9fafb"
                                        }}
                                    >
                                        <option value="today">Today</option>
                                        <option value="yesterday">Yesterday</option>
                                        <option value="week">This Week</option>
                                        <option value="month">This Month</option>
                                        <option value="custom">Custom</option>
                                    </select>
                                </div>

                            </Box>
                        </Box>
                        <TableContainer
                            style={{
                                maxHeight: "320px",
                                overflowX: "auto",   // ✅ horizontal scroll
                                overflowY: "auto",   // ✅ vertical scroll
                                whiteSpace: "nowrap" // ✅ prevents column wrapping
                            }}
                        >
                            <Table sx={{ minWidth: 1200 }}>
                                <TableHead
                                    sx={{
                                        position: "sticky",
                                        top: 0,
                                        zIndex: 10,
                                        backgroundColor: "#f97316",
                                    }}
                                >
                                    <TableRow>
                                        {/* ✅ LEFT FIXED */}
                                        <TableCell
                                            sx={{
                                                color: "#fff",
                                                position: "sticky",
                                                left: 0,
                                                background: "#f97316",
                                                zIndex: 11,
                                            }}
                                        >
                                            <b>Start Date</b>
                                        </TableCell>

                                        <TableCell
                                            sx={{
                                                color: "#fff",
                                                position: "sticky",
                                                left: 120,
                                                background: "#f97316",
                                                zIndex: 11,
                                            }}
                                        >
                                            <b>End Date</b>
                                        </TableCell>

                                        {/* 👉 SCROLLABLE MIDDLE */}
                                        <TableCell sx={{ color: "#fff" }}><b>Campaign Id</b></TableCell>
                                        <TableCell sx={{ color: "#fff" }}><b>Campaign Name</b></TableCell>
                                        <TableCell sx={{ color: "#fff" }}><b>Goal</b></TableCell>
                                        <TableCell sx={{ color: "#fff" }}><b>Sub Goal</b></TableCell>
                                        <TableCell sx={{ color: "#fff" }}><b>Medium</b></TableCell>
                                        <TableCell sx={{ color: "#fff" }}><b>Creativity</b></TableCell>

                                        <TableCell sx={{ color: "#fff" }}><b>Amount</b></TableCell>
                                        <TableCell sx={{ color: "#fff" }}><b>Status</b></TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                color: "#fff",
                                                position: "sticky",
                                                right: 120,
                                                minWidth: 70,
                                                background: "#f97316",
                                                zIndex: 12,
                                            }}
                                        >
                                            <b>Action</b>
                                        </TableCell>

                                        <TableCell
                                            align="center"
                                            sx={{
                                                color: "#fff",
                                                position: "sticky",
                                                right: 60,
                                                minWidth: 60,
                                                background: "#f97316",
                                                zIndex: 12,
                                            }}
                                        >
                                            <b>Invoice</b>
                                        </TableCell>

                                        <TableCell
                                            align="center"
                                            sx={{
                                                color: "#fff",
                                                position: "sticky",
                                                right: 0,
                                                minWidth: 60,
                                                background: "#f97316",
                                                zIndex: 12,
                                            }}
                                        >
                                            <b>View</b>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {getFilteredCampaigns().map((campaign) => (
                                        <TableRow key={campaign.id} sx={{ "&:hover": { background: "#fff7ed" } }}>

                                            {/* ✅ LEFT FIXED */}
                                            <TableCell
                                                sx={{
                                                    position: "sticky",
                                                    left: 0,
                                                    background: "#fff",
                                                    zIndex: 9,
                                                    minWidth: 120,
                                                }}
                                            >
                                                {new Date(campaign.startDate).toLocaleDateString("en-IN")}
                                            </TableCell>

                                            <TableCell
                                                sx={{
                                                    position: "sticky",
                                                    left: 120,
                                                    background: "#fff",
                                                    zIndex: 9,
                                                    minWidth: 120,
                                                }}
                                            >
                                                {new Date(campaign.endDate).toLocaleDateString("en-IN")}
                                            </TableCell>

                                            {/* 👉 SCROLLABLE  */}
                                            <TableCell>{campaign.campaignCode}</TableCell>
                                            <TableCell>{campaign.campaignName}</TableCell>
                                            <TableCell>{campaign.goal}</TableCell>
                                            <TableCell>{campaign.subGoal}</TableCell>
                                            <TableCell>{campaign.medium}</TableCell>
                                            <TableCell
                                                style={{ cursor: "pointer", color: "blue" }}
                                                onClick={() => {
                                                    let media = "";

                                                    if (campaign.mediaType === "VIDEO") {
                                                        media = campaign.mediaLink || campaign.imageUrl;
                                                    } else {
                                                        media = campaign.imageUrl;
                                                    }

                                                    setSelectedCreative(media);
                                                    setSelectedMediaType(campaign.mediaType); // ✅ IMPORTANT
                                                    setOpenCreative(true);
                                                }}
                                            >
                                                Creative
                                            </TableCell>
                                            <TableCell>{formatCurrency(campaign.totalBudget)}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={campaign.approvalStatus}
                                                    color={getStatusColor(campaign.approvalStatus)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    position: "sticky",
                                                    right: 120,
                                                    minWidth: 70,
                                                    background: "#fff",
                                                    zIndex: 10,
                                                }}
                                            >
                                                <IconButton
                                                    onClick={() => handleToggleCampaign(campaign.id)}
                                                    sx={{
                                                        background: campaign.isPaused ? "#dcfce7" : "#fee2e2",
                                                        "&:hover": {
                                                            background: campaign.isPaused ? "#bbf7d0" : "#fecaca",
                                                        },
                                                    }}
                                                >
                                                    {campaign.isPaused ? (
                                                        <PlayArrow color="success" />
                                                    ) : (
                                                        <Pause color="error" />
                                                    )}
                                                </IconButton>
                                            </TableCell>

                                            <TableCell
                                                align="center"
                                                sx={{
                                                    position: "sticky",
                                                    right: 60,
                                                    minWidth: 60,
                                                    background: "#fff",
                                                    zIndex: 10,
                                                }}
                                            >
                                                <IconButton onClick={() => handleOpenInvoice(campaign.id)}>
                                                    <FileText size={18} />
                                                </IconButton>
                                            </TableCell>

                                            <TableCell
                                                align="center"
                                                sx={{
                                                    position: "sticky",
                                                    right: 0,
                                                    minWidth: 60,
                                                    background: "#fff",
                                                    zIndex: 10,
                                                }}
                                            >
                                                <IconButton onClick={() => setSelectedCampaign(campaign)}>
                                                    <Eye size={18} />
                                                </IconButton>
                                            </TableCell>

                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>
                )}

                {/* DETAIL VIEW — shown in place of table */}
                {selectedCampaign && (
                    <CampaignDetailView
                        campaign={selectedCampaign}
                        onBack={() => setSelectedCampaign(null)}
                    />
                )}

            </Card>

            {/* ── Invoice Dialog — unchanged ── */}
            <Dialog
                open={openInvoice}
                onClose={() => setOpenInvoice(false)}
                // maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        maxWidth: "450px",
                        // background: "rgba(0,0,0,0.4)", // 🔥 overlay effect
                        boxShadow: "none",
                    },
                }}
            >
                <DialogContent
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        p: 2,
                        background: "transparent",
                    }}
                >
                    {loadingInvoice ? (
                        <Box textAlign="center" mt={5}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        invoiceData && (
                            <div
                                ref={invoiceRef}
                                style={{
                                    width: "420px",
                                    // background: "#ffffff",
                                    borderRadius: "12px",
                                    // padding: "20px",
                                    fontFamily: "Segoe UI",
                                    // border: "1px solid #e5e7eb",
                                }}
                            >
                                {/* HEADER */}
                                <div style={{
                                    position: "relative",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    marginBottom: "12px"
                                }}>

                                    {/* ✅ CENTERED TITLE */}
                                    <div style={{ textAlign: "center" }}>
                                        <div style={{ fontSize: "20px", fontWeight: "700" }}>
                                            INVOICE
                                        </div>
                                        <div style={{ fontSize: "12px", color: "#6b7280" }}>
                                            #{invoiceData.campaignCode}
                                        </div>
                                    </div>

                                    {/* ❌ Close button (RIGHT SIDE) */}
                                    <button
                                        onClick={() => setOpenInvoice(false)}
                                        style={{
                                            position: "absolute",
                                            right: "0",   // ✅ CHANGED HERE
                                            border: "none",
                                            background: "#f3f4f6",
                                            borderRadius: "6px",
                                            padding: "4px 8px",
                                            cursor: "pointer",
                                            fontSize: "14px"
                                        }}
                                    >
                                        ✕
                                    </button>

                                </div>

                                {/* DIVIDER */}
                                <div style={{
                                    height: "2px",
                                    background: "#111827",
                                    marginBottom: "12px"
                                }} />

                                {/* CONTENT */}
                                {[
                                    { label: "Campaign Name", value: invoiceData.campaignName },
                                    { label: "Campaign Code", value: invoiceData.campaignCode },
                                    { label: "Customer ID", value: invoiceData.customerId },
                                    { label: "Goal", value: invoiceData.goal },
                                    { label: "Start Date", value: new Date(invoiceData.startDate).toLocaleDateString("en-IN") },
                                    { label: "End Date", value: new Date(invoiceData.endDate).toLocaleDateString("en-IN") },
                                    { label: "Total Budget", value: `₹ ${invoiceData.totalBudget || 0}` },
                                ].map((item, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            display: "flex",
                                            justifyContent: "flex-start", // ✅ CHANGE
                                            gap: "150px",               // ✅ ADD SPACE
                                            padding: "8px 0",
                                            alignItems: "center"
                                        }}
                                    >
                                        <span style={{
                                            fontSize: "13px",
                                            color: "#6b7280",
                                            minWidth: "140px" // ✅ FIXED LABEL WIDTH
                                        }}>
                                            {item.label}
                                        </span>

                                        <span style={{
                                            fontSize: "13px",
                                            fontWeight: "600",
                                            textAlign: "left" // ✅ FORCE LEFT ALIGN
                                        }}>
                                            {item.value || "-"}
                                        </span>
                                    </div>
                                ))}

                                {/* FOOTER */}
                                <div style={{
                                    marginTop: "16px",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center"
                                }}>
                                    {/* <span style={{ fontSize: "11px", color: "#9ca3af" }}>
                                        Generated on {new Date().toLocaleDateString()}
                                    </span> */}

                                    <button
                                        onClick={downloadInvoice}
                                        style={{
                                            background: "#16a34a",
                                            color: "#fff",
                                            border: "none",
                                            padding: "6px 12px",
                                            borderRadius: "6px",
                                            fontSize: "12px",
                                            fontWeight: "600",
                                            justifyContent: "center",
                                            cursor: "pointer"
                                        }}
                                    >
                                        Download PDF
                                    </button>
                                </div>
                            </div>
                        )
                    )}
                </DialogContent>
            </Dialog>



            {openCreative && (
                <div style={overlayStyle}>
                    <div style={modalStyle}>

                        <button style={closeBtn} onClick={() => setOpenCreative(false)}>
                            ✖
                        </button>

                        {!selectedCreative ? (
                            <p>No creative available</p>
                        ) : selectedMediaType === "VIDEO" ? (
                            <video
                                src={selectedCreative}
                                controls
                                autoPlay
                                style={{ width: "100%", borderRadius: "10px" }}
                            />
                        ) : (
                            <img
                                src={selectedCreative}
                                alt="creative"
                                style={{
                                    width: "100%",
                                    height: "400px",
                                    borderRadius: "10px",
                                    objectFit: "cover"
                                }}
                            />
                        )}

                    </div>
                </div>
            )}

            <Dialog open={openDatePopup} onClose={() => setOpenDatePopup(false)}>
                <DialogContent
                    style={{
                        padding: "20px",
                        borderRadius: "12px",
                        minWidth: "300px",
                        fontFamily: "Segoe UI"
                    }}
                >



                    {/* DATE FIELDS */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>

                        {/* START DATE */}
                        <div>
                            <label style={{
                                fontSize: "12px",
                                color: "#6b7280",
                                fontWeight: "500"
                            }}>
                                Start Date
                            </label>

                            <input
                                type="date"
                                value={customStart}
                                onChange={(e) => setCustomStart(e.target.value)}
                                style={{
                                    width: "100%",
                                    marginTop: "4px",
                                    padding: "8px",
                                    borderRadius: "6px",
                                    border: "1px solid #e5e7eb",
                                    fontSize: "13px",
                                    outline: "none"
                                }}
                            />
                        </div>

                        {/* END DATE */}
                        <div>
                            <label style={{
                                fontSize: "12px",
                                color: "#6b7280",
                                fontWeight: "500"
                            }}>
                                End Date
                            </label>

                            <input
                                type="date"
                                value={customEnd}
                                onChange={(e) => setCustomEnd(e.target.value)}
                                style={{
                                    width: "100%",
                                    marginTop: "4px",
                                    padding: "8px",
                                    borderRadius: "6px",
                                    border: "1px solid #e5e7eb",
                                    fontSize: "13px",
                                    outline: "none"
                                }}
                            />
                        </div>

                    </div>

                    {/* BUTTONS */}
                    <div style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "10px",
                        marginTop: "18px"
                    }}>

                        {/* CANCEL */}
                        <button
                            onClick={() => setOpenDatePopup(false)}
                            style={{
                                padding: "6px 12px",
                                borderRadius: "6px",
                                border: "1px solid #e5e7eb",
                                background: "#fff",
                                cursor: "pointer",
                                fontSize: "13px"
                            }}
                        >
                            Cancel
                        </button>

                        {/* APPLY */}
                        <button
                            onClick={() => {
                                if (!customStart || !customEnd) {
                                    alert("Please select both dates");
                                    return;
                                }

                                setDateFilter("custom");
                                setOpenDatePopup(false);
                            }}
                            style={{
                                padding: "6px 14px",
                                borderRadius: "6px",
                                border: "none",
                                background: "#f97316",
                                color: "#fff",
                                fontWeight: "600",
                                cursor: "pointer",
                                fontSize: "13px"
                            }}
                        >
                            Apply
                        </button>

                    </div>

                </DialogContent>
            </Dialog>
        </Box>
    );
}
const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(0,0,0,0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999
};

const modalStyle = {
    width: "500px",
    background: "#fff",
    padding: "20px",

    borderRadius: "12px",
    position: "relative"
};

const closeBtn = {
    position: "absolute",
    top: "10px",
    right: "10px",
    border: "none",
    background: "#f97316",
    color: "#fff",
    padding: "6px 10px",
    cursor: "pointer",
    borderRadius: "6px"
};

const viewBtnStyle = {
    background: "#f97316",
    color: "#fff",
    border: "none",
    padding: "6px 10px",
    borderRadius: "6px",
    cursor: "pointer"
};



